import type { LucideIcon } from "lucide-react"
import { Box, Download, Eye, Images, Layers, Loader2, PenLine, Play, RefreshCw, Trash2, Upload, X } from "lucide-react"
import { useRef } from "react"
import { useTranslation } from "@/hooks/use-translation"
import { confirm } from "@/lib/confirm"
import { cn } from "@/lib/utils"

export const ASSET_OVERLAY_CHIP_CLASS =
  "z-20 flex items-center gap-0.5 rounded-md bg-[var(--ui-chip-bg-strong)] p-1 text-ui-text-primary shadow-md ring-1 ring-ui-border backdrop-blur-sm hover:bg-ui-accent/90 hover:text-ui-on-accent disabled:cursor-not-allowed disabled:opacity-50"

/** Компактный чип на thumb ракурса локации. */
export const ASSET_OVERLAY_CHIP_COMPACT_CLASS = "p-0.5 [&_svg]:h-3 [&_svg]:w-3"

/** Hover удаления — как `AssetCloseOverlayButton` на ракурсе. */
export const ASSET_OVERLAY_CHIP_DANGER_HOVER_CLASS = "hover:bg-red-600/80 hover:text-ui-on-accent"

const overlayChipClass = ASSET_OVERLAY_CHIP_CLASS

/** Кружок Play поверх постера видео. Клик обрабатывает родитель. */
export function PlayBadge({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "flex h-9 w-9 items-center justify-center rounded-full bg-[var(--ui-chip-bg-strong)] text-ui-text-primary ring-1 ring-ui-border backdrop-blur-sm",
        className,
      )}
    >
      <Play className="h-4 w-4 translate-x-[1px]" fill="currentColor" aria-hidden />
    </span>
  )
}

export function AssetVersionsBadge({
  count,
  onOpen,
  title,
  className,
  icon: Icon,
}: {
  count: number
  onOpen: () => void
  title: string
  className?: string
  icon?: LucideIcon
}) {
  const BadgeIcon = Icon ?? Layers

  return (
    <button
      type="button"
      className={cn(overlayChipClass, count > 0 && "pr-1.5", className)}
      aria-label={title}
      title={title}
      onClick={(e) => {
        e.stopPropagation()
        onOpen()
      }}
    >
      <BadgeIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
      {count > 0 ? (
        <span className="min-w-[0.875rem] text-center text-[10px] leading-none font-semibold tabular-nums">
          {count}
        </span>
      ) : null}
    </button>
  )
}

export function AssetLibraryOverlayButton({
  title,
  busy,
  onClick,
  className,
}: {
  title: string
  busy?: boolean
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      className={cn(overlayChipClass, className)}
      aria-label={title}
      title={title}
      disabled={busy}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
      ) : (
        <Images className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
      )}
    </button>
  )
}

export function AssetUploadOverlayButton({
  accept,
  uploading,
  title,
  onFile,
  className,
}: {
  accept: string
  uploading?: boolean
  title: string
  onFile: (file: File) => void | Promise<void>
  className?: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ""
          if (file) void onFile(file)
        }}
      />
      <button
        type="button"
        className={cn(overlayChipClass, className)}
        aria-label={title}
        title={title}
        disabled={uploading}
        onClick={(e) => {
          e.stopPropagation()
          inputRef.current?.click()
        }}
      >
        {uploading ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
        ) : (
          <Upload className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
        )}
      </button>
    </>
  )
}

export function AssetCorrectOverlayButton({
  title,
  disabled,
  disabledReason,
  onClick,
  className,
}: {
  title: string
  disabled?: boolean
  disabledReason?: string
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      className={cn(overlayChipClass, className)}
      aria-label={title}
      title={disabled && disabledReason ? disabledReason : title}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      <PenLine className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
    </button>
  )
}

export function AssetPromptOverlayButton({
  title,
  onClick,
  className,
}: {
  title: string
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      className={cn(overlayChipClass, className)}
      aria-label={title}
      title={title}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      <Eye className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
    </button>
  )
}

export function AssetDownloadOverlayButton({
  title,
  onClick,
  className,
}: {
  title: string
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      className={cn(overlayChipClass, className)}
      aria-label={title}
      title={title}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      <Download className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
    </button>
  )
}

export function AssetDeleteOverlayButton({
  title,
  onClick,
  className,
}: {
  title: string
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      className={cn(overlayChipClass, className)}
      aria-label={title}
      title={title}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      <Trash2 className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
    </button>
  )
}

/** Soft-clear active media version: confirm, then onClear. Gallery hard-delete keeps AssetDeleteOverlayButton. */
export function AssetClearOverlayButton({
  label,
  disabled,
  disabledReason,
  onClear,
  className,
}: {
  label?: string
  disabled?: boolean
  disabledReason?: string
  onClear: () => void
  className?: string
}) {
  const { t } = useTranslation(["workspace"])
  const title = label ?? t("assetMedia.clearVersion")

  const handleClick = () => {
    if (disabled) return
    void (async () => {
      if (
        !(await confirm({
          message: t("assetMedia.clearVersionConfirm"),
          confirmLabel: title,
          tone: "danger",
        }))
      ) {
        return
      }
      onClear()
    })()
  }

  return (
    <button
      type="button"
      className={cn(overlayChipClass, ASSET_OVERLAY_CHIP_DANGER_HOVER_CLASS, className)}
      aria-label={title}
      title={disabled && disabledReason ? disabledReason : title}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation()
        handleClick()
      }}
    >
      <Trash2 className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
    </button>
  )
}

export function AssetCloseOverlayButton({
  title,
  onClick,
  className,
}: {
  title: string
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      className={cn(overlayChipClass, ASSET_OVERLAY_CHIP_DANGER_HOVER_CLASS, className)}
      aria-label={title}
      title={title}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      <X className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
    </button>
  )
}

export function AssetRoomViewerOverlayButton({
  title,
  onClick,
  className,
}: {
  title: string
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      className={cn(overlayChipClass, className)}
      aria-label={title}
      title={title}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      <Box className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
    </button>
  )
}

export function AssetScene3dOverlayButton({
  title,
  onClick,
  className,
}: {
  title: string
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      className={cn(overlayChipClass, className)}
      aria-label={title}
      title={title}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      <PenLine className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
    </button>
  )
}

export function AssetRegenerateOverlayButton({
  title,
  disabled,
  disabledReason,
  busy,
  onClick,
  className,
  tone = "default",
}: {
  title: string
  disabled?: boolean
  disabledReason?: string
  busy?: boolean
  onClick: () => void
  className?: string
  tone?: "default" | "warning"
}) {
  return (
    <button
      type="button"
      className={cn(
        overlayChipClass,
        tone === "warning" && "text-amber-400",
        busy &&
          "cursor-default disabled:cursor-default disabled:hover:bg-[var(--ui-chip-bg-strong)] disabled:hover:text-ui-text-primary",
        className,
      )}
      aria-label={title}
      title={disabled && disabledReason ? disabledReason : title}
      disabled={disabled || busy}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
      ) : (
        <RefreshCw className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
      )}
    </button>
  )
}

export function AssetOverlayTopLeft({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn("pointer-events-auto absolute top-1.5 left-1.5 z-30 flex items-center gap-1", className)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  )
}

export function AssetOverlayTopRight({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn("pointer-events-auto absolute top-1.5 right-1.5 z-30 flex items-center gap-1", className)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  )
}
